import {
  enUS,
  esES,
  frFR,
  GridCellModes,
  gridStringOrNumberComparator,
  itIT,
  nlNL,
  roRO,
  viVN,
  zhCN,
} from '@mui/x-data-grid-pro';
import { GridColumnMenuFilterItem, GridColumnMenuPinningItem } from '@mui/x-data-grid-pro';
import { InputLabel, MenuItem, Select, Divider, IconButton, Stack } from '@mui/material';
import i18next from 'i18next';
import moment from 'moment-timezone';
import React from 'react';

import { ALLOWED_INPUT_KEYS } from '@commons/utils/numericInputKeys';
import { DATE_DISPLAY_FORMATS } from '@commons/DatePickers/constants';

export const DATAGRID_LOCALES = {
  cn: zhCN,
  en: enUS,
  fr: frFR,
  'fr-BE': frFR,
  it: itIT,
  ro: roRO,
  vi: viVN,
  nl: nlNL,
  es: esES,
};

/**
 * Return an array of dates as formatted strings between startDate and endDate (inclusive)
 * @param {string} startDate
 * @param {string} endDate
 * @param {string} dateFormat
 * @returns {string[]}
 *
 * ex: generateDates('2021-01-01', '2021-01-03') // ['2021-01-01', '2021-01-02', '2021-01-03']
 */
export const generateDates = (
  startDate,
  endDate,
  dateFormat = DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY,
) => {
  const dates = [];

  for (
    let currentDate = moment(startDate).clone();
    currentDate.isSameOrBefore(moment(endDate), 'day');
    currentDate.add(1, 'day')
  ) {
    dates.push(currentDate.format(dateFormat));
  }

  return dates;
};

/**
 * Custom sorter function for sorting values in a grid column.
 *
 * @param {any} valueA - The first value to compare.
 * @param {any} valueB - The second value to compare.
 * @param {object} cellParams1 - Additional parameters for the first cell.
 * @param {object} cellParams2 - Additional parameters for the second cell.
 * @returns {number} - The comparison result (-1, 0, or 1).
 */
export const customSorter = (valueA, valueB, cellParams1, cellParams2) => {
  if (!valueA || !valueB) {
    if (valueA === valueB) {
      return 0;
    } else {
      if (!valueA) {
        return 1;
      }

      return -1;
    }
  } else {
    return gridStringOrNumberComparator(
      valueA.toLowerCase(),
      valueB.toLowerCase(),
      cellParams1,
      cellParams2,
    );
  }
};

/**
 * Handle cell clicks to enable editing on touch devices
 * This is required for proper cell editing on tablets/iPads
 */
export const handleCellClick = (params, event, setCellModesModel) => {
  if (!params.isEditable) {
    return;
  }

  // Ignore portal
  if (event.target.nodeType === 1 && !event.currentTarget.contains(event.target)) {
    return;
  }

  setCellModesModel((prevModel) => ({
    // Revert the mode of the other cells from other rows
    ...Object.keys(prevModel).reduce(
      (acc, id) => ({
        ...acc,
        [id]: Object.keys(prevModel[id]).reduce(
          (acc2, field) => ({
            ...acc2,
            [field]: { mode: GridCellModes.View },
          }),
          {},
        ),
      }),
      {},
    ),
    [params.id]: {
      // Revert the mode of other cells in the same row
      ...Object.keys(prevModel[params.id] || {}).reduce(
        (acc, field) => ({ ...acc, [field]: { mode: GridCellModes.View } }),
        {},
      ),
      [params.field]: { mode: GridCellModes.Edit },
    },
  }));
};

export const handleCellEditStop = (params, setCellModesModel) => {
  // Allow immediate row update after blur event
  if (params.reason === 'cellFocusOut') {
    setCellModesModel((prev) => ({
      ...prev,
      [params.id]: {
        ...prev[params.id],
        [params.field]: { mode: GridCellModes.View },
      },
    }));
  }
};

export const booleanSelectOperators = [
  {
    value: 'is',
    getApplyFilterFn: (filterItem) => {
      if (filterItem.value == null || filterItem.value === '') {
        return null;
      }

      return (params) => {
        const cellValue = params.value;
        const targetValue = Boolean(filterItem.value);
        const actualValue = Boolean(cellValue);

        return actualValue === targetValue;
      };
    },
    InputComponent: (props) => (
      <>
        <InputLabel></InputLabel>
        <Select
          sx={{ minWidth: 120 }}
          value={props.item.value ?? ''}
          displayEmpty
          onChange={(event) => {
            const selectedValue = event.target.value;

            props.applyValue({
              ...props.item,
              value: selectedValue === '' ? undefined : selectedValue === 'true',
            });
          }}
        >
          <MenuItem value="">
            <em>--</em>
          </MenuItem>
          <MenuItem value="true">{i18next.t('GENERAL.YES')}</MenuItem>
          <MenuItem value="false">{i18next.t('GENERAL.NO')}</MenuItem>
        </Select>
      </>
    ),
    getValueAsString: (value) => {
      if (value == null || value === '') return '';
      return i18next.t(value ? 'GENERAL.YES' : 'GENERAL.NO');
    },
  },
];

/**
 * Creates select filter operators for columns with predefined options
 * @param {Array} options - Array of unique values for the dropdown
 * @param {string} noneLabel - Label to show when no value is selected
 * @returns {Array} Array of filter operators
 */
export const createSelectOperators = (options = [], noneLabel = '--') => [
  {
    value: 'is',
    getApplyFilterFn: (filterItem) => {
      if (filterItem.value == null || filterItem.value === '') {
        return null;
      }

      if (filterItem.value === i18next.t('GENERAL.NONE_VALUE')) {
        return ({ value }) => value === null || value === '';
      }

      return ({ value }) => value === filterItem.value;
    },
    InputComponent: (props) => (
      <>
        <InputLabel></InputLabel>
        <Select
          sx={{ minWidth: 180 }}
          value={props.item.value ?? ''}
          displayEmpty
          onChange={(event) =>
            props.applyValue({
              ...props.item,
              value: event.target.value,
            })
          }
        >
          <MenuItem value="">
            <em>{noneLabel}</em>
          </MenuItem>
          {options.sort().map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      </>
    ),
  },
];

export const CustomMenuIcon = () => (
  <IconButton>
    <img src={'/images/inpulse/carret-black.svg'} style={{ width: '16px', height: '16px' }} />
  </IconButton>
);

export const CustomColumnMenu = (props, pinnedFields) => {
  const { colDef, hideMenu } = props;

  const pinnedItemProps = {
    colDef,
    onClick: hideMenu,
  };

  return (
    <>
      {pinnedFields.includes(colDef.field) && (
        <Stack>
          <GridColumnMenuPinningItem {...pinnedItemProps} />
          <Divider />
          <GridColumnMenuFilterItem {...pinnedItemProps} />
        </Stack>
      )}
      {!pinnedFields.includes(colDef.field) && null}
    </>
  );
};

// Extract unique values from data by key
export const getUniqueValuesByKey = (data, key) => {
  const uniqueValues = new Set();

  data.forEach((item) => {
    if (item[key]) {
      uniqueValues.add(item[key]);
    }
  });
  return Array.from(uniqueValues).sort();
};

// See https://mui.com/x/react-data-grid/style/#using-the-sx-prop for reference
export const DATAGRID_COMMON_STYLE = {
  maxHeight: 'calc(100% - 48px)',
  height: 'auto',
  minWidth: '886px',
  marginBottom: '24px',
  // Override default styles of Datagrid
  '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
    outline: 'none !important',
  },
  '&.MuiDataGrid-root .MuiDataGrid-row:hover': {
    backgroundColor: 'transparent !important',
  },
  '&.MuiDataGrid-root .MuiDataGrid-row.Mui-hovered': {
    backgroundColor: 'transparent !important',
  },
  '&.MuiDataGrid-root .MuiDataGrid-cell.MuiDataGrid-cell--editing': {
    padding: '10px !important',
  },
};

// Usefull for dataGrid input cells that only allow positive numbers
export const handleKeyDown = (e) => {
  // Only allow digits, backspace, delete, and navigation keys
  if (!ALLOWED_INPUT_KEYS.has(e.key)) {
    e.preventDefault();
  }
};

export const DATAGRID_PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

export const MENU_PAPER_STYLE = {
  boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.16)',
  borderRadius: '8px',
  marginTop: '4px',
};
